import { DashboardTypes, subCampaignResponse } from 'API';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCampaignId, useChildCampaignId, useSubCampaignId } from './useParameters';
import { fetchSubCampaignsFunction } from 'methods/fetchSubCampaigns';
import { ACdetails } from 'context/CampaignContext';

export const useActiveSubCampaign = (
  dashType: DashboardTypes | null,
  activeCampaign: ACdetails,
): readonly [subCampaignResponse | undefined, subCampaignResponse[], Error | undefined] => {
  const [campID] = useCampaignId();
  const [childCampID] = useChildCampaignId();

  const campaignID = childCampID !== 'none' ? childCampID : campID;
  const [activeSubCampaign, setActiveSubCampaign] = useState<subCampaignResponse | undefined>(
    {} as subCampaignResponse,
  );
  const [subCampaignList, setSubCampaignList] = useState<subCampaignResponse[]>([] as subCampaignResponse[]);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const [getSubCampaignId] = useSubCampaignId();
  const navigate = useNavigate();
  const searchParam = 'subcampaignID';

  // Use window.location.search instead of the closure-captured searchParams to always read
  // the latest URL params. This prevents a race where the async subcampaign fetch completes
  // after InputContext has already set videoSource in the URL but before searchParams has
  // re-rendered with that update.
  const setId2 = (value: string, link: string): void => {
    const newSearchParams = new URLSearchParams(window.location.search);
    newSearchParams.set(searchParam, value);
    const finalSearch = newSearchParams.toString();
    navigate(link + '?' + finalSearch);
  };

  let activeSubCampaignId = searchParams.get(searchParam) ?? undefined;

  useEffect(() => {
    const fetchSubCampaignsList = async () => {
      // if the campaignID is none, set the subCampaignList to an empty array
      if (campaignID === 'none' || (dashType !== DashboardTypes.TV && dashType !== DashboardTypes.RADIO)) {
        setSubCampaignList([]);
        return;
      }

      // fetch the subCampaigns
      const result = await fetchSubCampaignsFunction(campaignID, dashType);
      activeSubCampaignId = undefined;

      if (result && result.length > 0) {
        const filteredResult1 = result.filter((item): item is subCampaignResponse => item !== null);
        const filteredResult = filteredResult1.filter((item): item is subCampaignResponse => {
          if (activeCampaign?.authorized) {
            return true;
          }
          return !!activeCampaign?.subcampaign[item.subcampaignId as number];
        });
        setSubCampaignList(filteredResult);

        if (result !== null) {
          testCall(filteredResult);
        }
      } else {
        setSubCampaignList([]);
      }
    };

    // Always run the clear logic, regardless of activeCampaign
    if (campaignID === 'none' || (dashType !== DashboardTypes.TV && dashType !== DashboardTypes.RADIO)) {
      setSubCampaignList([]);
      return;
    }

    if (activeCampaign) {
      fetchSubCampaignsList();
    }
  }, [campaignID, dashType, activeCampaign]);

  const testCall = (subCampaignList: Array<subCampaignResponse>) => {
    if (activeSubCampaignId !== undefined || getSubCampaignId !== 'none') {
      return;
    }

    // BVOD does not rely on TV subcampaigns. If BVOD is the active video source, skip
    // the TV subcampaign auto-selection to avoid incorrectly overwriting the URL and
    // triggering the wrong published-data fetch in PublishContext.
    const currentVideoSource = new URLSearchParams(window.location.search).get('videoSource');
    if (currentVideoSource === 'BVOD') {
      return;
    }

    const firstSubCampaign = subCampaignList[0];
    if (!firstSubCampaign) {
      return;
    }

    const firstSubCampaignId = firstSubCampaign.subcampaignId;
    if (!firstSubCampaignId) {
      return;
    }

    const portalPath =
      dashType === DashboardTypes.TV
        ? '/reporting/TVPortal'
        : dashType === DashboardTypes.RADIO
          ? '/reporting/radioPortal'
          : null;

    if (portalPath) {
      setId2(String(firstSubCampaignId), portalPath);
    }
  };

  // When the user switches from BVOD to TV, the subcampaign list is already loaded but
  // testCall was skipped earlier (BVOD guard). This effect re-runs testCall whenever
  // videoSource or the subcampaign list changes, so switching to TV auto-selects the first subcampaign.
  // When switching to BVOD, subcampaignID is cleared from the URL since BVOD doesn't use it.
  const videoSource = searchParams.get('videoSource');
  useEffect(() => {
    if (videoSource === 'BVOD') {
      const newSearchParams = new URLSearchParams(window.location.search);
      if (newSearchParams.has(searchParam)) {
        newSearchParams.delete(searchParam);
        setSearchParams(newSearchParams);
      }
      return;
    }
    if (subCampaignList.length === 0) return;
    if (getSubCampaignId !== 'none') return;
    testCall(subCampaignList);
  }, [videoSource, subCampaignList]);

  // if the activeSubCampaignId is 'none' and the subCampaignList is not empty, set the activeSubCampaign to the first subCampaign in the list
  // else if the activeSubCampaignId is not 'none' and the subCampaignList is not empty, set the activeSubCampaign to the subCampaign with the id of activeSubCampaignId

  useEffect(() => {
    if (activeCampaign) {
      const noSubCampaign: subCampaignResponse = {
        __typename: 'subCampaignResponse',
        subcampaignId: -1,
        name: undefined,
        startDate: undefined,
        endDate: undefined,
        mediaGroup: undefined,
      };

      if (subCampaignList.length === 0 && dashType !== DashboardTypes.HOME) {
        setActiveSubCampaign(noSubCampaign);
        return;
      }

      if (subCampaignList.length === 0 && dashType !== DashboardTypes.HOME) {
        setActiveSubCampaign(undefined);
        return;
      }

      if (subCampaignList.length === 0) {
        setActiveSubCampaign(undefined);
        return;
      }

      const firstSubCampaign = subCampaignList[0];
      const firstSubCampaignId = firstSubCampaign.subcampaignId;

      if (!firstSubCampaignId) {
        return;
      }

      if (activeSubCampaignId === 'none' && (dashType === DashboardTypes.TV || dashType === DashboardTypes.RADIO)) {
        setActiveSubCampaign(firstSubCampaign);
        if (dashType === DashboardTypes.TV) {
          setId2(String(firstSubCampaignId), '/reporting/TVPortal');
        } else if (dashType === DashboardTypes.RADIO) {
          setId2(String(firstSubCampaignId), '/reporting/radioPortal');
        }
        return;
      }

      if (activeSubCampaignId !== 'none' && (dashType === DashboardTypes.TV || dashType === DashboardTypes.RADIO)) {
        const activeSubCamapign = subCampaignList.find(
          (subCampaign) => subCampaign.subcampaignId === Number(activeSubCampaignId),
        );
        if (activeSubCamapign) {
          setActiveSubCampaign(activeSubCamapign);
        }
      }
    }
  }, [activeSubCampaignId, subCampaignList, dashType, activeCampaign]);

  return [activeSubCampaign, subCampaignList, error];
};
